import React, {useEffect, useMemo, useRef} from 'react';
import {styled, BaseProvider} from 'baseui';
import {IconContext} from 'react-icons';

import Modal from 'react-modal';
import {IntercomProvider} from 'react-use-intercom';
import Header from './Header';
import './layout.css';
import Fonts from './Fonts';
import Analytics from './Analytics';
import theme from '../utils/theme';
import Footer from './Footer';
import {getUserCurrency} from '../utils/storeGeoLocator';
import {useStoreRegionCurrency, useUserIdentification} from '../utils/hooks';
import {getStripe} from '../utils/stripe';
import initErrorLogging from '../utils/errorLogging';
import Cookiebanner from './Cookiebanner';
import {
  handleMixpanelAnalytics,
  checkUserIdFromUrlQueryParam,
  MixpanelEvents,
} from '../utils/events';
import ProductSelectGuide from './ProductSelectGuide';
import {useNavigation} from '../context/NavigationContext';

Modal.setAppElement('#___gatsby');

type Props = {
  children: React.ReactNode | React.ReactNode[];
  isFullWidth?: boolean;
  isFrontPage?: boolean;
  isStorePage?: boolean;
  hideNavigation?: boolean;
  skuIdentifier?: string;
  generic?: GatsbyTypes.ContentfulStoreTextGeneric;
  onMenuPress?: () => any;
  priceString?: string;
  onOrderPress?: () => any;
  isLoading?: boolean;
  showProductSelectInFooter?: boolean;
  canCheckoutViaConfigurator?: boolean;
  subMenuTitle?: string;
  regionChangeTrigger?: string | undefined;
  hideFooter?: boolean;
  hideHeader?: boolean;
  hideNavigationButtons?: boolean;
};

const LayoutWrapper = styled('div', () => ({
  margin: '0 auto',
  maxWidth: '960px',
  minHeight: '600px',
  padding: '0 1.0875rem 1.45rem',
}));

const HeaderSpacer = styled('div', () => ({
  height: '109px',
}));

const HeaderSpacerFront = styled('div', () => ({
  height: '58px',
}));

function Layout({
  children,
  isFullWidth,
  isFrontPage,
  isStorePage,
  onMenuPress,
  hideNavigation,
  skuIdentifier,
  generic,
  priceString,
  onOrderPress,
  isLoading,
  showProductSelectInFooter,
  canCheckoutViaConfigurator,
  subMenuTitle,
  regionChangeTrigger,
  hideFooter,
  hideHeader,
  hideNavigationButtons,
}: Props) {
  const [storeData, setStoreData] = useStoreRegionCurrency();
  const derivedStoreRegion =
    storeData && storeData.length > 0 ? storeData[0].region : undefined;
  const storeDataRef = useRef(storeData);
  const navigation = useNavigation();

  // Checks uid from email every 10 min
  useUserIdentification();

  // We need to store storeData in a ref to make it accessible from the useEffect callback
  useEffect(() => {
    storeDataRef.current = storeData;
  }, [derivedStoreRegion]);

  const getUserCountry = async () => {
    // Check ref for updates as localStorage is not populated on first render
    if (storeDataRef.current) return;

    const res = await getUserCurrency();
    setStoreData([res]);
  };

  const iconContextValue = useMemo(
    () => ({
      style: {verticalAlign: 'middle'},
    }),
    []
  );

  const triggerPoint = useMemo(
    () => navigation.history[navigation.history.length - 1],
    [navigation.history]
  );

  useEffect(() => {
    initErrorLogging();
    checkUserIdFromUrlQueryParam();

    if (isStorePage) {
      getStripe();
    }

    // Set a small timeout to allow localStorage to update local ref before checking if
    // we need to fetch information about the user's current current country
    setTimeout(() => {
      getUserCountry();
    }, 1000);
  }, []);

  // When you first open the website, navigation history is undefined
  // Therefore, we will trigger when length is defined and length has changed
  useEffect(() => {
    if (!navigation.history.length) return;

    handleMixpanelAnalytics(MixpanelEvents.PAGE_VIEWED, {
      slug: triggerPoint,
    });
  }, [navigation.history.length]);

  const onShow = () => {
    handleMixpanelAnalytics(MixpanelEvents.CHAT_OPENED, {
      chat_trigger_point: triggerPoint === '/' ? 'Homepage' : triggerPoint,
    });
  };

  return (
    <BaseProvider theme={theme}>
      <IconContext.Provider value={iconContextValue}>
        <IntercomProvider appId='rgo5g0vn' autoBoot onShow={onShow}>
          <Analytics />
          <Fonts />
          <Header
            isStorePage={isStorePage}
            onMenuPress={onMenuPress}
            hideNavigation={hideNavigation}
            removeHeader={hideHeader}
            skuIdentifier={skuIdentifier}
            generic={generic}
            priceString={priceString}
            onOrderPress={onOrderPress}
            isLoading={isLoading}
            canCheckoutViaConfigurator={canCheckoutViaConfigurator}
            subMenuTitle={subMenuTitle}
            regionChangeTrigger={regionChangeTrigger}
            hideNavigationButtons={hideNavigationButtons}
          />
          {isFrontPage ? <HeaderSpacerFront /> : null}
          {isFrontPage || hideNavigation || hideHeader ? null : (
            <HeaderSpacer />
          )}

          {isFullWidth ? (
            <main>{children}</main>
          ) : (
            <LayoutWrapper>
              <main>{children}</main>
            </LayoutWrapper>
          )}
          {showProductSelectInFooter ? <ProductSelectGuide /> : null}
          {!hideNavigation && !hideFooter ? <Footer /> : null}
          {isFrontPage ? <Cookiebanner /> : null}
        </IntercomProvider>
      </IconContext.Provider>
    </BaseProvider>
  );
}

export default Layout;
